import { Link } from 'react-router-dom';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import { SupportPrompt } from '../support/SupportPrompt';
import { WELLBEING_PATHS } from '../wellbeingPaths';
import type { BreatheFeeling } from './breatheTypes';

const FEELINGS: { id: BreatheFeeling; label: string }[] = [
  { id: 'calmer', label: 'Calmer' },
  { id: 'same', label: 'About the same' },
  { id: 'tense', label: 'Still tense' },
];

const REPLY: Record<BreatheFeeling, string> = {
  calmer: 'Good. That calm is a great place to start learning from.',
  same: "That's okay. It often takes a couple of rounds to land.",
  tense: "Thank you for noticing. Let's make it smaller.",
};

export interface BreatheDoneProps {
  /** What the session added up to, said back as a small win. */
  summary: { breaths: number; minutes: number };
  feeling: BreatheFeeling | null;
  onFeeling: (feeling: BreatheFeeling) => void;
  onAgain: () => void;
}

function NextStep({
  feeling,
  onAgain,
}: {
  feeling: BreatheFeeling;
  onAgain: () => void;
}) {
  const again = (
    <Button
      className='max-sm:h-11'
      variant={feeling === 'calmer' ? 'outline' : 'default'}
      onClick={onAgain}
    >
      <RotateCcw className='mr-2 h-4 w-4' aria-hidden='true' />
      Go again
    </Button>
  );
  return (
    <div className='space-y-4'>
      <p className='text-foreground'>{REPLY[feeling]}</p>
      <div className='flex flex-wrap gap-2'>
        {feeling === 'calmer' ? (
          <>
            <Button className='max-sm:h-11' asChild>
              <Link to={WELLBEING_PATHS.focus}>
                Start a focus block
                <ArrowRight className='ml-1.5 h-4 w-4' aria-hidden='true' />
              </Link>
            </Button>
            {again}
          </>
        ) : (
          <>
            {again}
            <Button className='max-sm:h-11' asChild variant='outline'>
              <Link to={WELLBEING_PATHS.worry}>
                Write down what&apos;s on your mind
              </Link>
            </Button>
          </>
        )}
      </div>
      {feeling === 'tense' && <SupportPrompt stacked />}
    </div>
  );
}

/**
 * After a session: a moment to notice, then one next step that fits what
 * the student noticed. Calmer leads to a focus block; anything else offers
 * another round or writing it down - and still tense, people to talk to.
 */
export function BreatheDone({
  summary,
  feeling,
  onFeeling,
  onAgain,
}: BreatheDoneProps) {
  return (
    <div
      className='w-full max-w-md space-y-6 justify-self-center lg:justify-self-start'
      aria-live='polite'
    >
      <div>
        <h2 className='text-xl font-semibold text-foreground'>Nicely done.</h2>
        <p className='mt-1 text-foreground'>
          You took {summary.breaths} slow{' '}
          {summary.breaths === 1 ? 'breath' : 'breaths'} in {summary.minutes}{' '}
          {summary.minutes === 1 ? 'minute' : 'minutes'}.
        </p>
        <p className='mt-3 text-muted-foreground'>How do you feel now?</p>
      </div>
      <div
        className='flex flex-wrap gap-2'
        role='group'
        aria-label='How do you feel now?'
      >
        {FEELINGS.map(option => (
          <button
            key={option.id}
            type='button'
            aria-pressed={feeling === option.id}
            onClick={() => onFeeling(option.id)}
            className={cn(
              'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-base',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
              feeling === option.id
                ? 'bg-primary text-primary-foreground dark:bg-brand-300 dark:text-brand-950'
                : 'border border-border bg-card text-foreground hover:bg-secondary'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      {feeling ? (
        <NextStep feeling={feeling} onAgain={onAgain} />
      ) : (
        <Button className='max-sm:h-11' variant='outline' onClick={onAgain}>
          <RotateCcw className='mr-2 h-4 w-4' aria-hidden='true' />
          Go again
        </Button>
      )}
    </div>
  );
}
